import { Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';
import { formatFocusArea } from './config/interviewConfig';

/**
 * Score icon based on average performance
 */
const ScoreIcon = ({ avgScore }) => {
  if (avgScore >= 8) {
    return (
      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mr-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 text-green-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  } else if (avgScore >= 6) {
    return (
      <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mr-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 text-blue-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905a3.61 3.61 0 01-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
          />
        </svg>
      </div>
    );
  } else if (avgScore >= 4) {
    return (
      <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mr-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 text-yellow-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
    );
  } else {
    return (
      <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mr-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 text-red-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
    );
  }
};

ScoreIcon.propTypes = {
  avgScore: PropTypes.number.isRequired,
};

/**
 * Total score section with progress bar
 */
const TotalScoreSection = ({ feedback }) => {
  if (!feedback?.length) return null;

  const totalScore = feedback.reduce((sum, item) => sum + item.score, 0);
  const maxPossibleScore = feedback.length * 10;
  const avgScore = totalScore / feedback.length;
  const percentage = (totalScore / maxPossibleScore) * 100;

  let scoreDescription;
  if (avgScore >= 8) scoreDescription = 'Excellent';
  else if (avgScore >= 6) scoreDescription = 'Good';
  else if (avgScore >= 4) scoreDescription = 'Needs Improvement';
  else scoreDescription = 'Requires Significant Work';

  let barColor;
  if (percentage >= 80) barColor = 'bg-green-500';
  else if (percentage >= 60) barColor = 'bg-blue-500';
  else if (percentage >= 40) barColor = 'bg-yellow-500';
  else barColor = 'bg-red-500';

  return (
    <div className="mb-6 p-4 border rounded-lg bg-white shadow-sm">
      <div className="flex flex-col items-center sm:flex-row sm:justify-between">
        <div className="flex items-center mb-4 sm:mb-0">
          <ScoreIcon avgScore={avgScore} />
          <div>
            <p className="text-md text-gray-600">
              <span className="font-bold">{totalScore}</span> out of {maxPossibleScore} points (
              {scoreDescription})
            </p>
          </div>
        </div>

        <div className="w-full sm:w-1/3 bg-gray-200 rounded-full h-4">
          <div className={`h-4 rounded-full ${barColor}`} style={{ width: `${percentage}%` }} />
        </div>
      </div>
    </div>
  );
};

TotalScoreSection.propTypes = {
  feedback: PropTypes.arrayOf(
    PropTypes.shape({
      score: PropTypes.number.isRequired,
    })
  ),
};

/**
 * Focus area summary grid
 */
const FocusAreaSummary = ({ focusAreaSummary }) => {
  if (!focusAreaSummary) return null;

  return (
    <div className="mb-6 p-4 border rounded-lg bg-white shadow-sm">
      <h4 className="text-lg font-semibold text-gray-800 mb-3">Focus Areas Performance</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(focusAreaSummary).map(([area, data]) => {
          const getBarColor = (score) => {
            if (score === null) return 'bg-gray-400';
            if (score >= 8) return 'bg-green-500';
            if (score >= 6) return 'bg-blue-500';
            if (score >= 4) return 'bg-yellow-500';
            return 'bg-red-500';
          };

          return (
            <div key={area} className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium text-gray-700">{formatFocusArea(area)}</h5>
                <span className="text-sm font-bold text-gray-600">
                  {data.score !== null ? `${data.score}/10` : 'Not Evaluated'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div
                  className={`h-2 rounded-full ${getBarColor(data.score)}`}
                  style={{ width: data.score !== null ? `${(data.score / 10) * 100}%` : '100%' }}
                />
              </div>
              <p className="text-xs text-gray-600">{data.notes}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

FocusAreaSummary.propTypes = {
  focusAreaSummary: PropTypes.objectOf(
    PropTypes.shape({
      score: PropTypes.number,
      notes: PropTypes.string,
    })
  ),
};

/**
 * Individual feedback card for each question
 */
const FeedbackCard = ({ item, index, userResponses }) => {
  const getBarColor = (score) => {
    if (score >= 8) return 'bg-green-500';
    if (score >= 6) return 'bg-blue-500';
    if (score >= 4) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-white shadow-sm mb-3">
      <h4 className="font-semibold text-gray-800">{item.question}</h4>

      {/* Score bar */}
      <div className="flex items-center mb-2">
        <span className="font-medium text-gray-700 mr-3">Score:</span>
        <div className="flex items-center">
          <div className="w-20 bg-gray-200 rounded-full h-2.5 mr-2">
            <div
              className={`h-2.5 rounded-full ${getBarColor(item.score)}`}
              style={{ width: `${(item.score / 10) * 100}%` }}
            />
          </div>
          <span className="text-sm font-medium text-gray-700">{item.score}/10</span>
        </div>
      </div>

      {/* User's original response(s) */}
      <div className="mb-4 p-3 bg-gray-50 border border-gray-100 rounded-lg">
        <h5 className="font-medium text-gray-700 mb-2">
          Your Response{userResponses.length > 1 ? 's' : ''}:
        </h5>
        {userResponses.length > 0 ? (
          userResponses.map((response, respIdx) => (
            <div key={respIdx} className="mb-2">
              {userResponses.length > 1 && (
                <p className="text-xs text-gray-500 mb-1">Response {respIdx + 1}:</p>
              )}
              <p className="text-sm text-gray-600">{response}</p>
            </div>
          ))
        ) : (
          <p className="text-sm text-gray-600 italic">No response found</p>
        )}
      </div>

      {/* Focus Areas Addressed */}
      {item.focus_areas_addressed && (
        <div className="mb-3">
          <span className="font-medium text-gray-700 mr-2">Focus Areas:</span>
          {item.focus_areas_addressed.map((area, idx) => (
            <span
              key={idx}
              className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-1"
            >
              {formatFocusArea(area)}
            </span>
          ))}
        </div>
      )}

      {/* Strengths and Improvements grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        <div className="bg-green-50 p-3 rounded-lg">
          <h5 className="font-medium text-green-700 mb-2">Strengths</h5>
          <ul className="list-disc pl-5 space-y-1">
            {item.strengths.map((strength, i) => (
              <li key={i} className="text-sm text-gray-600">
                {strength}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-amber-50 p-3 rounded-lg">
          <h5 className="font-medium text-amber-700 mb-2">Areas for Improvement</h5>
          <ul className="list-disc pl-5 space-y-1">
            {item.areas_for_improvement.map((area, i) => (
              <li key={i} className="text-sm text-gray-600">
                {area}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Suggestions */}
      <div className="bg-blue-50 p-3 rounded-lg">
        <h5 className="font-medium text-blue-700 mb-2">Suggestions</h5>
        <ul className="list-disc pl-5 space-y-1">
          {item.suggestions.map((suggestion, i) => (
            <li key={i} className="text-sm text-gray-600">
              {suggestion}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

FeedbackCard.propTypes = {
  item: PropTypes.shape({
    question: PropTypes.string.isRequired,
    score: PropTypes.number.isRequired,
    strengths: PropTypes.arrayOf(PropTypes.string).isRequired,
    areas_for_improvement: PropTypes.arrayOf(PropTypes.string).isRequired,
    suggestions: PropTypes.arrayOf(PropTypes.string).isRequired,
    focus_areas_addressed: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  index: PropTypes.number.isRequired,
  userResponses: PropTypes.arrayOf(PropTypes.string).isRequired,
};

/**
 * Helper to find user responses for a question
 */
const findUserResponses = (item, gptMessages) => {
  const questionIndices = [];
  let userResponses = [];

  // Find all occurrences of the question in the transcript
  gptMessages.forEach((msg, idx) => {
    if (msg.role === 'assistant' && msg.content.includes(item.question)) {
      questionIndices.push(idx);
    }
  });

  // For each question occurrence, look for user responses that follow
  questionIndices.forEach((qIndex) => {
    if (qIndex + 1 < gptMessages.length && gptMessages[qIndex + 1].role === 'user') {
      userResponses.push(gptMessages[qIndex + 1].content);
    }
  });

  // If exact matching failed, try a more flexible approach
  if (userResponses.length === 0) {
    const questionWords = item.question
      .split(' ')
      .filter((word) => word.length > 3)
      .map((word) => word.toLowerCase());

    gptMessages.forEach((msg, idx) => {
      if (msg.role === 'assistant') {
        const messageContent = msg.content.toLowerCase();
        const matchingWords = questionWords.filter((word) =>
          messageContent.includes(word.toLowerCase())
        );

        if (matchingWords.length >= Math.min(3, questionWords.length)) {
          if (idx + 1 < gptMessages.length && gptMessages[idx + 1].role === 'user') {
            userResponses.push(gptMessages[idx + 1].content);
          }
        }
      }
    });
  }

  return userResponses;
};

/**
 * Main Interview Feedback component
 */
const InterviewFeedback = ({
  selectedCompany,
  focusAreas,
  feedback,
  feedbackLoading,
  feedbackError,
  onRetryFeedback,
  gptMessages,
  onExportToQABank,
  onRestartInterview,
}) => {
  return (
    <div className="flex flex-col flex-grow items-center justify-center p-4 h-full">
      <div className="flex flex-col items-center p-6 bg-white rounded-xl shadow-md border border-green-100 max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between w-full mb-6 flex-shrink-0">
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {selectedCompany} Interview Completed!
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Focus areas evaluated: {focusAreas.map((area) => formatFocusArea(area)).join(', ')}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onExportToQABank}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors shadow-md"
            >
              Export to Q&A Bank
            </button>
            <button
              onClick={onRestartInterview}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-md"
            >
              Start Over
            </button>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto w-full">
          {feedbackLoading && (
            <div className="w-full py-8 flex flex-col items-center">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin mb-4" />
              <p className="text-gray-500">Generating feedback...</p>
            </div>
          )}

          {feedbackError && (
            <div className="w-full p-4 mb-6 bg-red-50 border border-red-100 rounded-lg">
              <p className="text-red-600">{feedbackError}</p>
              {/* Without this the failure is a dead end: the transcript is
                  still in memory and the analysis is retryable, but the only
                  other actions on this screen discard the interview. */}
              {onRetryFeedback && (
                <button
                  type="button"
                  onClick={onRetryFeedback}
                  className="mt-3 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                >
                  Try again
                </button>
              )}
            </div>
          )}

          {feedback && !feedbackLoading && (
            <div className="w-full">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Your Interview Feedback</h3>

              <TotalScoreSection feedback={feedback.feedback} />
              <FocusAreaSummary focusAreaSummary={feedback.focus_area_summary} />

              <div className="space-y-6">
                {feedback.feedback.map((item, index) => (
                  <FeedbackCard
                    key={index}
                    item={item}
                    index={index}
                    userResponses={findUserResponses(item, gptMessages)}
                  />
                ))}

                <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                  <h4 className="font-semibold text-gray-800 mb-2">Overall Feedback</h4>
                  <p className="text-gray-600">{feedback.overall_feedback}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

InterviewFeedback.propTypes = {
  selectedCompany: PropTypes.string.isRequired,
  focusAreas: PropTypes.arrayOf(PropTypes.string).isRequired,
  feedback: PropTypes.shape({
    feedback: PropTypes.arrayOf(
      PropTypes.shape({
        question: PropTypes.string,
        score: PropTypes.number,
        strengths: PropTypes.arrayOf(PropTypes.string),
        areas_for_improvement: PropTypes.arrayOf(PropTypes.string),
        suggestions: PropTypes.arrayOf(PropTypes.string),
        focus_areas_addressed: PropTypes.arrayOf(PropTypes.string),
      })
    ),
    focus_area_summary: PropTypes.object,
    overall_feedback: PropTypes.string,
  }),
  feedbackLoading: PropTypes.bool.isRequired,
  feedbackError: PropTypes.string,
  onRetryFeedback: PropTypes.func,
  gptMessages: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.string,
      content: PropTypes.string,
    })
  ).isRequired,
  onExportToQABank: PropTypes.func.isRequired,
  onRestartInterview: PropTypes.func.isRequired,
};

InterviewFeedback.defaultProps = {
  feedback: null,
  feedbackError: null,
  onRetryFeedback: null,
};

export default InterviewFeedback;
